import React, { useState, useEffect, useRef } from 'react';

import { Modal, Table, Form, Input } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { NotificationInstance } from 'antd/es/notification/interface';
import { LoadingOutlined, ReloadOutlined, EditOutlined, DeleteOutlined, PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons';

import Toolbar from '@controls/toolbar/toolbar';
import Filter from '@controls/filter/filter';

import { exception, securityRestriction } from '@extensions/notification';
import { delayAction } from '@extensions/utils';

import { userLoaded, setFilter } from '@store/actions';
import { useAppSelector, useAppDispatch } from '@store/hooks';

import { serverFetch } from '@flc/server';

import { IUserSession } from '@entities/user-session';
import { ISite } from '@entities/site';

import { Permission, hasPermission } from '@enums/permission';

interface ISitesProps {
    cityId: string | undefined;
    api: NotificationInstance;
}

const Sites = (props: ISitesProps) => {
    const { TextArea } = Input;

    const { cityId, api } = props;

    const [siteForm] = Form.useForm();

    const [modal, modalContextHolder] = Modal.useModal();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [sites, setSites] = useState<Array<ISite>>([]);
    const [currentSite, setCurrentSite] = useState<ISite>();

    const [selectedIds, setSelectedIds] = useState<React.Key[]>([]);
    const [refreshRequired, setRefreshRequired] = useState<boolean>(true);
    const [loading, setLoading] = useState<boolean>(false);

    const [siteOpen, setSiteOpen] = useState<boolean>(false);

    useEffect(() => {
        if (!cityId) {
            setSites([]);
            return;
        }

        setRefreshRequired(true);
    }, [cityId]);

    useEffect(() => {
        if (!refreshRequired) return;

        setRefreshRequired(false);

        setSites([]);
        setSelectedIds([]);
        setCurrentSite(undefined);

        setLoading(true);

        serverFetch('sites', { method: 'GET', queryParams: { cityId: cityId } })
            .then((data) => {
                setSites(data);
                setLoading(false);
            })
            .catch((ex) => {
                exception(api, 'Ошибка получения адресов разгрузки', ex, () => d(userLoaded(undefined)));
                setLoading(false);
            });
    }, [refreshRequired]);

    const onDelete = () => {
        if (!currentSite) return;

        serverFetch(`sites/${currentSite?.id}`, { method: 'DELETE' })
            .then(() => {
                onSelectChange([]);
                setRefreshRequired(true);
            })
            .catch((ex) => {
                exception(api, 'Ошибка удаления адреса разгрузки', ex, () => d(userLoaded(undefined)));
            });
    };

    const onSave = (data: ISite) => {
        setLoading(true);

        serverFetch(`sites`, { method: data.id ? 'PUT' : 'POST', bodyData: data })
            .then(() => {
                setLoading(false);
                setRefreshRequired(true);

                siteForm.resetFields();
                setSiteOpen(false);
            })
            .catch((ex) => {
                setLoading(false);
                exception(api, 'Ошибка сохранения адреса разгрузки', ex, () => d(userLoaded(undefined)));
            });
    };

    const renderToolbar = () => {
        return (
            <Toolbar
                commands={[
                    {
                        label: 'Обновить',
                        key: 'refresh',
                        disabled: loading,
                        icon: <ReloadOutlined />,
                        onClick: () => {
                            setRefreshRequired(true);
                        },
                    },
                    {
                        type: 'primary',
                        label: 'Добавить',
                        key: 'add',
                        icon: <PlusOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageSites)) {
                                securityRestriction(api, [Permission.ManageSites]);
                                return;
                            }

                            setCurrentSite(undefined);
                            setSiteOpen(true);
                        },
                    },
                    {
                        label: 'Изменить',
                        key: 'edit',
                        disabled: !currentSite,
                        icon: <EditOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.ManageSites)) {
                                securityRestriction(api, [Permission.ManageSites]);
                                return;
                            }

                            setSiteOpen(true);
                        },
                    },
                    {
                        label: 'Удалить',
                        key: 'delete',
                        disabled: !currentSite,
                        icon: <DeleteOutlined />,
                        onClick: () => {
                            if (!hasPermission(userSession.permissions, Permission.DeleteSite)) {
                                securityRestriction(api, [Permission.DeleteSite]);
                                return;
                            }

                            modal.confirm({
                                title: `Удалить адрес разгрузки "${currentSite?.address}"?`,
                                icon: <QuestionCircleOutlined />,
                                okType: 'primary',
                                okText: 'Удалить',
                                cancelText: 'Отмена',
                                onOk: () => {
                                    onDelete();
                                },
                            });
                        },
                    },
                ]}
            />
        );
    };

    const onSelectChange = (selectedRowKeys: React.Key[]) => {
        setSelectedIds(selectedRowKeys);

        if (selectedRowKeys.length == 1) {
            const entity = sites.find((o) => o.id == selectedRowKeys[0]);
            setCurrentSite(entity);
        } else {
            setCurrentSite(undefined);
        }
    };

    const renderTable = () => {
        const columns: ColumnsType<ISite> = [
            {
                title: 'Адрес',
                dataIndex: 'address',
                width: 600,
            },
            {},
        ];

        const tableLoading = {
            spinning: loading,
            indicator: <LoadingOutlined style={{ fontSize: 44 }} spin />,
        };

        return (
            <Table
                showHeader={false}
                rowKey='id'
                size='small'
                rowSelection={{
                    selectedRowKeys: selectedIds,
                    onChange: onSelectChange,
                    type: 'radio',
                }}
                columns={columns}
                dataSource={sites}
                loading={tableLoading}
                onRow={(record: ISite) => {
                    return {
                        onClick: (event) => {
                            if (!record.id) return;

                            onSelectChange([record.id]);
                        },
                    };
                }}
                pagination={false}
                scroll={{ y: 'calc(100vh - 192px)' }}
            />
        );
    };

    return (
        <>
            {renderToolbar()}
            {renderTable()}

            <Modal
                destroyOnHidden={true}
                closable={false}
                title={currentSite ? 'Изменить адрес разгрузки' : 'Добавить адрес разгрузки'}
                open={siteOpen}
                okText='ОК'
                onOk={() => siteForm.submit()}
                onCancel={() => {
                    siteForm.resetFields();
                    setSiteOpen(false);
                }}
                width={500}
            >
                <Form
                    preserve={false}
                    colon={false}
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 18 }}
                    style={{ marginTop: 20 }}
                    onFinish={onSave}
                    form={siteForm}
                >
                    <Form.Item hidden name='id' initialValue={currentSite?.id}>
                        <Input />
                    </Form.Item>
                    <Form.Item hidden name='cityId' initialValue={cityId}>
                        <Input />
                    </Form.Item>

                    <Form.Item
                        initialValue={currentSite?.address}
                        label='Адрес'
                        name='address'
                        required={true}
                        rules={[{ required: true, message: 'Укажите адрес' }]}
                    >
                        <TextArea autoFocus rows={2} />
                    </Form.Item>
                </Form>
            </Modal>

            {modalContextHolder}
        </>
    );
};

export default Sites;
